import { Box, Flex, Text } from '@chakra-ui/layout';
import { useQuery } from '@tanstack/react-query';
import { useQuery as useUrqlQuery } from 'urql';

import { useAuthStore } from '@revelio/auth';
import {
  type CustomRoleTaxonomySelection,
  ROLE_K17000_SELECTION_LIST,
} from '@revelio/data-access';
import { useChartTheme } from '@revelio/replots';

import { environment } from '../../../../environments/environment';
import { TanstackQueryError } from '../../../../shared/data-fetch/tanstack-query-provider';
import { useEffectiveFilters } from '../../filters/hooks/useEffectiveFilters';
import {
  FilterId,
  type PrimaryFilterId,
  type SingleValueFilter,
} from '../../filters/types/filters';
import { generateSentimentPayload } from '../../filters/utils/generateSentimentReviewPayload';
import { usePlotMetric } from '../../hooks/usePlotMetric';
import { useSelectedPrimaryEntity } from '../../hooks/useSelectedPrimaryEntity';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import { usePageState } from '../PageState/PageStateProvider';
import { ChartLoader } from './ChartLoader';
import { useChart } from './ChartProvider';
import {
  isChartLoadingFromSwitcher,
  useClearChartSwitcherPlotWhenDone,
} from './hooks/useChartSwitcherPlot';

type SentimentProps = {
  primaryDimension: PrimaryFilterId;
};

export const SentimentReview = ({ primaryDimension }: SentimentProps) => {
  const primaryEntity = useSelectedPrimaryEntity();
  const metric = usePlotMetric();
  const { isNewTheme, theme } = useChartTheme();

  const { dashMetaCsrfToken: csrfToken = 'missing' } = useAuthStore();
  const effectiveFilters = useEffectiveFilters();

  const { config } = useTabContentConfig();
  const isSnapshotDate = (() => {
    return (
      config.id === 'company-sentiment-snapshot' ||
      config.id === 'geography-sentiment-snapshot' ||
      config.id === 'role-sentiment-snapshot'
    );
  })();

  const roleTaxonomyFilterState = effectiveFilters.find(
    (filter) => filter.id === FilterId.ROLE_TAXONOMY
  ) as SingleValueFilter<CustomRoleTaxonomySelection>;
  const [{ data: roleK17000SelectionList }] = useUrqlQuery({
    query: ROLE_K17000_SELECTION_LIST,
    variables: { taxonomyId: roleTaxonomyFilterState?.value },
  });

  const payload = (() => {
    if (!primaryEntity) return null;

    return generateSentimentPayload({
      filters: effectiveFilters,
      roleK17000SelectionList,
      primaryFilter: primaryDimension,
      reviewType: metric === 'negative_review' ? 'neg' : 'pos',
      selectedEntity: {
        id: Number(primaryEntity.id),
        label: primaryEntity.name,
        selectionListId: primaryEntity.levelId,
      },
      isSnapshotDate,
    });
  })();

  const { data, isLoading } = useQuery({
    queryKey: ['sentiment-review', payload],
    queryFn: async () => {
      if (!payload) return null;
      const requestID = crypto.randomUUID();

      const response = await fetch(
        `${environment.DASH_META_API}/api/sentiment/reviews`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'x-surf-token-dash-meta': csrfToken,
            'request-id': requestID,
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        throw new TanstackQueryError(
          'Failed to fetch sentiment reviews',
          requestID
        );
      }

      return response.json();
    },
    enabled: !!payload,
    staleTime: 1000 * 60 * 60, // 1 hour
  });

  useClearChartSwitcherPlotWhenDone(!isLoading);

  const { item } = useChart();
  const { pageState } = usePageState();
  const cardScopedLoader = isChartLoadingFromSwitcher(
    item.id,
    pageState.chartSwitcherPlotId
  );

  return (
    <Box
      height="100%"
      width="100%"
      {...(cardScopedLoader && { position: 'relative' })}
    >
      {isLoading && <ChartLoader />}
      <Flex
        flexDirection="column"
        padding={0}
        margin={0}
        justifyContent="flex-start"
        maxHeight="100%"
        height="100%"
        overflow="auto"
        sx={{
          maskImage: 'linear-gradient(to bottom, black 87%, transparent 100%);',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background:
              isNewTheme && theme === 'dark'
                ? 'rgba(255, 255, 255, 0.2)'
                : 'gray.200',
            borderRadius: '4px',
          },
        }}
      >
        <Flex
          justifyContent={isNewTheme ? 'flex-start' : 'center'}
          alignItems={isNewTheme ? 'flex-start' : 'center'}
          height="100%"
          backgroundColor={isNewTheme ? 'transparent' : 'white'}
        >
          <Text
            color={isNewTheme ? 'var(--ui-text-normal)' : undefined}
            sx={{ pb: '16px' }}
          >
            {data?.review || data?.error || ''}
          </Text>
        </Flex>
      </Flex>
    </Box>
  );
};
